import { useMemo } from 'react';
import Line from '../../../Components/Charts/Line/Line';

export default function ReadinessChart({ chartDataSource, filteredData, warmupCutoff = null }) {
    const dataPerf = useMemo(() => {
        const labels = chartDataSource.map(p =>
            new Date(p.workoutStart).toLocaleDateString('el-GR')
        );
        const currentDateTimestamp = Date.now();
        const pastWorkouts = chartDataSource.filter(
            workout => new Date(workout.workoutStart).getTime() <= currentDateTimestamp
        );
        const futureWorkouts = chartDataSource.slice(pastWorkouts.length - 1);
        const atlData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.atl
        }));

        const ctlData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.ctl
        }));

        const tsbData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.tsb
        }));

        const tssData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.tss
        }));

        const futureAtlData = futureWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.atl
        }));
        const futureCtlData = futureWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.ctl
        }));
        const futureTsbData = futureWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.tsb
        }));

        return {
            labels,
            datasets: [
                {
                    type: 'line',
                    label: 'Fatigue (ATL)',
                    borderColor: `rgb(234, 43, 35)`,
                    borderWidth: 2,
                    fill: false,
                    data: atlData,
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Future Fatigue (ATL)',
                    borderColor: `rgba(234, 43, 35, 0.5)`,
                    borderWidth: 2,
                    borderDash: [5, 5],
                    fill: false,
                    data: futureAtlData,
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Fitness (CTL)',
                    borderColor: `rgb(34, 168, 53)`,
                    borderWidth: 2,
                    fill: false,
                    data: ctlData,
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Future Fitness (CTL)',
                    borderColor: `rgba(34, 168, 53, 0.5)`,
                    borderWidth: 2,
                    fill: false,
                    data: futureCtlData,
                    borderDash: [5, 5],
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Form (TSB)',
                    borderColor: `rgb(88, 05, 251)`,
                    borderWidth: 2,
                    fill: false,
                    data: tsbData,
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Future Form (TSB)',
                    borderColor: `rgba(88, 05, 251, 0.5)`,
                    borderWidth: 2,
                    fill: false,
                    data: futureTsbData,
                    spanGaps: true,
                    borderDash: [5, 5],
                    yAxisID: 'y1'
                },
                {
                    type: 'bar',
                    label: 'Stress (TSS)',
                    backgroundColor: `rgb(42, 85, 244)`,
                    borderColor: `rgb(42, 85, 244)`,
                    data: tssData,
                    maxBarThickness: 50,
                    yAxisID: 'y'
                }
            ],
            pastWorkouts,
            futureWorkouts
        };
    }, [chartDataSource]);

    const readinessChartDataset = useMemo(() => {
        const cutoffTime = warmupCutoff ? new Date(warmupCutoff).getTime() : null;
        // Keep every date on the axis (so it stays aligned with the performance chart
        // above), but hide the ACWR value while ATL/CTL are still warming up — during
        // that window the ratio ramps up from zero and shows a false spike.
        const isWarmup = workout =>
            cutoffTime != null && new Date(workout.workoutStart).getTime() < cutoffTime;

        const acwrData = dataPerf.pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: isWarmup(workout) ? null : workout.acwr
        }));
        const futureAcwrData = dataPerf.futureWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: isWarmup(workout) ? null : workout.acwr
        }));

        return [
            {
                label: 'ACWR',
                data: acwrData,
                borderColor: `rgb(1,1,1)`,
                backgroundColor: `rgba((1,1,1), 0.5)`,
                spanGaps: true
            },
            {
                label: 'Future ACWR',
                data: futureAcwrData,
                borderColor: `rgb(1,1,1)`,
                backgroundColor: `rgba((1,1,1), 0.5)`,
                spanGaps: true,
                borderDash: [5, 5]
            }
        ];
    }, [dataPerf, warmupCutoff]);

    const annotations = useMemo(() => {
        // Average of the actual (non-warm-up, non-future) ACWR points.
        const filteredAcwr = readinessChartDataset[0].data
            .map(point => point.y)
            .filter(value => typeof value === 'number');

        const Sum = filteredAcwr.reduce((a, b) => a + b, 0);

        const avgAcwr = filteredAcwr.length > 0 ? Sum / filteredAcwr.length : 0;

        return [
            // Light blue zone: 0 - 0.8 (Undertraining)
            {
                type: 'box',
                xScaleID: 'x',
                yScaleID: 'y',
                yMin: 0,
                yMax: 0.8,
                backgroundColor: 'rgba(3, 169, 244, 0.15)',
                borderColor: 'rgba(3, 169, 244, 0.4)',
                borderWidth: 0
            },
            // Green zone: 0.8 - 1.3 (Optimum)
            {
                type: 'box',
                xScaleID: 'x',
                yScaleID: 'y',
                yMin: 0.8,
                yMax: 1.3,
                backgroundColor: 'rgba(34, 168, 53, 0.15)',
                borderColor: 'rgba(34, 168, 53, 0.4)',
                borderWidth: 0
            },
            // Yellow zone: 1.3 - 1.5 (Over Reaching)
            {
                type: 'box',
                xScaleID: 'x',
                yScaleID: 'y',
                yMin: 1.3,
                yMax: 1.5,
                backgroundColor: 'rgba(255, 193, 7, 0.15)',
                borderColor: 'rgba(255, 193, 7, 0.4)',
                borderWidth: 0
            },
            // Red zone: > 1.5 (Overtraining)
            {
                type: 'box',
                xScaleID: 'x',
                yScaleID: 'y',
                yMin: 1.5,
                yMax: 4,
                backgroundColor: 'rgba(234, 43, 35, 0.15)',
                borderColor: 'rgba(234, 43, 35, 0.4)',
                borderWidth: 0
            },
            // Average line
            {
                type: 'line',
                borderColor: 'rgb(25, 118, 210)',
                borderDashOffset: 0,
                borderWidth: 2,
                label: {
                    display: true,
                    content: 'Average: ' + avgAcwr.toFixed(2),
                    position: 'end',
                    backgroundColor: 'rgba(25, 118, 210, 0.9)',
                    color: 'white',
                    padding: 4
                },
                scaleID: 'y',
                value: avgAcwr
            },
            // Boundary lines with labels
            {
                type: 'line',
                yMin: 1.5,
                yMax: 1.5,
                borderColor: 'rgba(234, 43, 35, 0.8)',
                borderWidth: 2,
                borderDash: [6, 6],
                label: {
                    display: true,
                    content: '> 1.5 Overtraining',
                    position: 'start',
                    backgroundColor: 'rgba(234, 43, 35, 0.9)',
                    color: 'white',
                    padding: 4
                }
            },
            {
                type: 'line',
                yMin: 1.3,
                yMax: 1.3,
                borderColor: 'rgba(255, 193, 7, 0.8)',
                borderWidth: 2,
                borderDash: [6, 6],
                label: {
                    display: true,
                    content: '1.3 - 1.5 Over Reaching',
                    position: 'start',
                    backgroundColor: 'rgba(255, 193, 7, 0.9)',
                    color: 'rgba(0, 0, 0, 0.87)',
                    padding: 4
                }
            },
            {
                type: 'line',
                yMin: 0.8,
                yMax: 0.8,
                borderColor: 'rgba(34, 168, 53, 0.8)',
                borderWidth: 2,
                borderDash: [6, 6],
                label: {
                    display: true,
                    content: '0.8 - 1.3 Optimum',
                    position: 'start',
                    backgroundColor: 'rgba(34, 168, 53, 0.9)',
                    color: 'white',
                    padding: 4
                }
            },
            {
                type: 'line',
                yMin: 0,
                yMax: 0,
                borderColor: 'rgba(158, 158, 158, 0.6)',
                borderWidth: 1,
                label: {
                    display: true,
                    content: '< 0.8 Undertraining',
                    position: 'start',
                    backgroundColor: 'rgba(158, 158, 158, 0.9)',
                    color: 'white',
                    padding: 4
                }
            }
        ];
    }, [readinessChartDataset]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: window.innerWidth >= 480,
                position: window.innerWidth < 768 ? 'bottom' : 'top',
                labels: {
                    usePointStyle: true,
                    padding: window.innerWidth < 768 ? 8 : 10,
                    font: {
                        size: window.innerWidth < 768 ? 10 : 12
                    },
                    generateLabels: chart => {
                        return chart.data.datasets
                            .map((dataset, i) => ({
                                text: dataset.label,
                                strokeStyle: dataset.borderColor || dataset.backgroundColor,
                                fillStyle: dataset.borderColor || dataset.backgroundColor,
                                pointStyle: 'line',
                                hidden: !chart.isDatasetVisible(i),
                                datasetIndex: i
                            }))
                            .filter(label => !label.text.includes('Future'));
                    }
                }
            }
        },
        scales: {
            x: {
                ticks: {
                    maxRotation: window.innerWidth < 768 ? 45 : 0,
                    minRotation: window.innerWidth < 768 ? 45 : 0,
                    font: {
                        size: window.innerWidth < 768 ? 9 : 11
                    }
                }
            },
            y: {
                min: 0,
                max: 4,
                ticks: {
                    font: {
                        size: window.innerWidth < 768 ? 9 : 11
                    }
                }
            }
        }
    };

    return (
        <div
            style={{
                width: '100%',
                height: window.innerWidth < 768 ? '500px' : '500px',
                maxHeight: window.innerWidth < 768 ? 'none' : '50vh',
                padding: window.innerWidth < 768 ? '10px' : '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: window.innerWidth < 768 ? '10px' : '20px',
                position: 'relative'
            }}
        >
            {window.innerWidth < 768 && (
                <div
                    style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'rgba(51, 78, 172, 0.9)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '600',
                        zIndex: 10,
                        pointerEvents: 'none'
                    }}
                >
                    Pinch to zoom
                </div>
            )}
            <Line
                titleY={'ACWR'}
                titleX={'Workout Dates'}
                datasets={readinessChartDataset}
                labels={dataPerf.labels}
                annotations={annotations}
                xAxisType={'category'}
                options={chartOptions}
            />
        </div>
    );
}
